import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Award, Briefcase } from 'lucide-react';
import FadeIn from '../components/FadeIn';

const AboutPage = () => {
  const skills = {
    core: [
      'Data Analysis',
      'AI/ML',
      'Big Data',
      'Predictive Modeling',
      'Data Warehousing',
      'ETL Pipelines',
      'Hypothesis Testing',
      'A/B Testing',
      'KPI Definition',
      'Product Management',
      'UX Design'
    ],
    tech: [
      'Python (Pandas, NumPy, Scikit-Learn, Matplotlib, TensorFlow, Keras)',
      'SQL',
      'R',
      'AWS',
      'GCP',
      'BigQuery',
      'Spark',
      'Hadoop',
      'Tableau',
      'Power BI',
      'MySQL',
      'Firebase',
      'Cursor',
      'VSCode',
      'Jira',
      'MS-Office',
      'G-Suite'
    ]
  };

  const experiences = [
    {
      title: 'Co-Founder & Head of Product',
      company: 'Ohara Labs AI',
      location: 'Berlin',
      period: 'Jan 2026 – Present',
      isCurrent: true,
      achievements: [
        'Analytics Architecture: Designed and deployed a real-time analytics engine to track 15+ Product KPIs, enabling data-driven iteration of the product roadmap.',
        'User Behavior Analysis: Utilized engagement data to fine-tune RAG retrieval parameters, directly improving AI tutor response accuracy and user retention.',
        'Orchestrated the development of an AI-first learning platform targeting 200K+ students, reducing curriculum generation latency by <60s through performance optimization.'
      ]
    },
    {
      title: 'Software Engineer',
      company: 'Doxel',
      location: 'Bengaluru',
      period: 'June 2023 – April 2024',
      isCurrent: false,
      achievements: [
        'Engineered models that identified 80% of critical schedule delays in construction projects, allowing for proactive data-driven interventions.',
        'Implemented A/B testing cycles for a site schematic annotation tool, analyzing usage patterns to increase labeling throughput by 25%.'
      ]
    },
    {
      title: 'Software Engineer',
      company: 'CGI',
      location: 'Bengaluru',
      period: 'July 2021 – March 2023',
      isCurrent: false,
      achievements: [
        'Scaled a loan application platform supporting 500,000 Monthly Active Users (MAU), optimizing database queries in PostgreSQL to handle high-volume transaction data.',
        'Automated E2E testing pipelines, generating performance data that led to a 15% increase in operational efficiency.'
      ]
    }
  ];

  const certifications = [
    { name: 'Google Data Analytics Professional Certificate', date: 'Aug 2025' },
    { name: 'AWS Educate Machine Learning Foundations', date: 'Aug 2025' },
    { name: 'Udacity Generative AI with AWS', date: 'Aug 2025' },
    { name: 'Project Management Professional', org: 'BSBI' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white py-20 px-6">
      <div className="max-w-4xl mx-auto space-y-16">
        {/* Profile Image Section */}
        <FadeIn direction="down" className="flex flex-col items-center text-center">
          <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-gray-200 dark:border-gray-800 shadow-xl mb-12">
            <img
              src="https://customer-assets.emergentagent.com/job_dc30b116-dcfb-43c4-b9b7-5db372078b51/artifacts/ce8xlc3f_Gemini_Generated_Image_m3pkyum3pkyum3pk.png"
              alt="Pradeepta Dey"
              className="w-full h-full object-cover"
            />
          </div>
        </FadeIn>

        {/* About Section */}
        <FadeIn delay={0.2}>
          <section>
            <h1 className="text-5xl md:text-6xl font-light mb-12 tracking-tight">About Me</h1>
            <div className="space-y-6 text-lg leading-relaxed text-gray-700 dark:text-gray-300">
              <p>
                I am a <span className="text-black dark:text-white font-medium">Data Analyst</span> based in Berlin, currently pursuing my MSc in Data Analytics at BSBI. After starting my career in Software engineering, I realized that the most valuable asset in any application is not the code, but <span className="text-black dark:text-white font-medium">the data it generates</span>.
              </p>
              <p>
                Today, I leverage my educational & professional background to build robust <span className="text-black dark:text-white font-medium">data ecosystems</span>. Whether it's architecting real-time analytics engines for AI products or engineering predictive models for enterprise risk management, I bridge the gap between raw data and strategic decision-making.
              </p>
              <p>
                I am passionate about using <span className="text-black dark:text-white font-medium">statistical methods and machine learning</span> to solve real-world problems.
              </p>
            </div>
          </section>
        </FadeIn>

        <Separator />

        {/* Core Competencies Section */}
        <FadeIn delay={0.3}>
          <section>
            <h2 className="text-4xl md:text-5xl font-light mb-12 tracking-tight">Core Competencies</h2>
            
            <div className="space-y-8">
              {/* Core Skills */}
              <div>
                <h3 className="text-2xl font-light mb-6">Core Skills</h3>
                <div className="flex flex-wrap gap-3">
                  {skills.core.map((skill, index) => (
                    <Badge key={index} variant="outline" className="px-4 py-2 text-sm font-normal hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Tech Stack */}
              <div>
                <h3 className="text-2xl font-light mb-6">Tech Stack</h3>
                <div className="flex flex-wrap gap-3">
                  {skills.tech.map((tech, index) => (
                    <Badge key={index} variant="secondary" className="px-4 py-2 text-sm font-normal hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors">
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </FadeIn>

        <Separator />

        {/* Professional Experience Section */}
        <FadeIn delay={0.4}>
          <section>
            <h2 className="text-4xl md:text-5xl font-light mb-12 tracking-tight flex items-center gap-3">
              <Briefcase size={40} className="text-gray-400" />
              Professional Experience
            </h2>
            <div className="space-y-8">
              {experiences.map((exp, index) => (
                <Card key={index} className="border-none shadow-none bg-gray-50 dark:bg-gray-950">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-2xl font-normal mb-2">{exp.title}</CardTitle>
                        <CardDescription className="text-base">
                          {exp.company} ({exp.location})
                        </CardDescription>
                        <CardDescription className="text-sm text-gray-500 mt-1">
                          {exp.period}
                        </CardDescription>
                      </div>
                      {exp.isCurrent && (
                        <Badge variant="outline" className="text-sm">Current</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                      {exp.achievements.map((achievement, idx) => (
                        <p key={idx}>• {achievement}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </FadeIn>

        <Separator />

        {/* Certifications Section */}
        <FadeIn delay={0.5}>
          <section>
            <h2 className="text-4xl md:text-5xl font-light mb-12 tracking-tight flex items-center gap-3">
              <Award size={40} className="text-gray-400" />
              Certifications
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {certifications.map((cert, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600 transition-colors">
                  <Award size={20} className="mt-1 text-gray-500" />
                  <div className="flex-1">
                    <div className="font-normal text-base">{cert.name}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {cert.org || ''} {cert.date ? `• ${cert.date}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </FadeIn>
      </div>
    </div>
  );
};

export default AboutPage;